import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { inr, inrFull } from "../../lib/format";
import { Sparkle, ArrowsClockwise, Receipt, Info, Bank } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";

/**
 * NetWorthV2Panel — bundles 3 Phase A features inside Portfolio page:
 *   • Pre/Post-Tax toggle + per-property tax estimate (Enh 3)
 *   • Auto Snapshot trend chart (Enh 5)
 *   • AI Advisor ranked recommendations (Enh 4)
 *
 * Auto-fires the idempotent /networth/snapshot on mount so first-of-month
 * captures happen with zero user effort.
 */
export default function NetWorthV2Panel({ goalAmount = null }) {
  const [postTax, setPostTax] = useState(null);
  const [taxOn, setTaxOn] = useState(false);
  const [snaps, setSnaps] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [advisorBusy, setAdvisorBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [willCov, setWillCov] = useState(null);

  // Fire-and-forget monthly snapshot on mount, then refresh history
  useEffect(() => {
    (async () => {
      try { await api.post("/networth/snapshot"); } catch { /* ignore */ }
      try {
        const { data } = await api.get("/networth/snapshots?months=24");
        setSnaps(data);
      } catch { /* ignore */ }
    })();
  }, []);

  // Load post-tax + cached advisor up-front
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/networth/post-tax");
        setPostTax(data);
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/networth/ai-advisor/latest");
        setAdvisor(data);
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/calc/will-coverage");
        setWillCov(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const generateAdvisor = useCallback(async () => {
    setAdvisorBusy(true);
    try {
      const { data } = await api.post("/networth/ai-advisor/generate");
      setAdvisor(data);
    } catch (e) {
      setAdvisor({ error: e?.response?.data?.detail || "AI advisor unavailable" });
    } finally { setAdvisorBusy(false); }
  }, []);

  return (
    <div className="space-y-10 mb-10" data-testid="networth-v2-panel">
      {willCov && willCov.total > 0 && (
        <div className={`card-flat p-4 flex items-baseline justify-between gap-3 flex-wrap ${
          willCov.covered === willCov.total
            ? "bg-green-500/5 border-l-4 border-l-green-500"
            : "bg-amber-500/5 border-l-4 border-l-amber-500"
        }`} data-testid="will-coverage-banner">
          <div>
            <div className="text-sm">
              <b>{willCov.covered}</b> of <b>{willCov.total}</b> properties are covered by a registered/notarised Will.
            </div>
            {willCov.covered < willCov.total && (
              <div className="text-xs text-muted-foreground mt-1">
                Properties not covered will pass via Hindu Succession Act / intestate rules — usually slower and more contested.
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid="will-coverage-items">
              {willCov.items.map((it) => (
                <span key={it.id} className={`px-2 py-1 rounded-full border ${
                  it.status === "covered" ? "bg-green-500/15 text-green-700 border-green-500/30"
                  : it.status === "partial" ? "bg-amber-500/15 text-amber-700 border-amber-500/30 animate-pulse"
                  : "bg-red-500/15 text-red-700 border-red-500/30 animate-pulse"
                }`} data-testid={`will-coverage-pill-${it.id}`}>
                  {it.status === "covered" ? "🟢" : it.status === "partial" ? "🟡" : "🔴"}
                  &nbsp;{it.name}
                </span>
              ))}
            </div>
          </div>
          <a href="/app/will" className="btn-primary px-3 py-1.5 text-xs"
            data-testid="will-coverage-cta">
            {willCov.has_will ? "Update your Will →" : "Complete your estate plan →"}
          </a>
        </div>
      )}

      {/* === Post-Tax Toggle === */}
      {postTax && postTax.total_current_value > 0 && (
        <div className="card-flat p-6" data-testid="post-tax-section">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
            <div>
              <div className="eyebrow mb-1 inline-flex items-center gap-2">
                <Receipt size={12} weight="duotone" /> Post-tax net worth
              </div>
              <div className="font-serif text-2xl">
                What you&apos;d actually keep if you sold today.
              </div>
            </div>
            <div className="inline-flex items-center gap-2 text-xs">
              <button
                onClick={() => setSettingsOpen(true)}
                className="btn-ghost px-2 py-1.5"
                data-testid="post-tax-settings-btn">
                Tax settings ({postTax.tax_regime} regime · {postTax.income_slab})
              </button>
              <div className="inline-flex border hairline rounded overflow-hidden">
                <button
                  onClick={() => setTaxOn(false)}
                  className={`px-3 py-1.5 ${!taxOn ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                  data-testid="post-tax-toggle-pre">Pre-tax</button>
                <button
                  onClick={() => setTaxOn(true)}
                  className={`px-3 py-1.5 ${taxOn ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                  data-testid="post-tax-toggle-post">Post-tax</button>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <BigStat label={taxOn ? "Post-tax net worth" : "Pre-tax net worth"}
              value={inrFull(taxOn ? postTax.post_tax_net_worth : postTax.pre_tax_net_worth)}
              testid="post-tax-headline" />
            <BigStat label="Est. tax liability"
              value={inrFull(postTax.total_tax_liability)}
              accent={postTax.total_tax_liability > 0 ? "warn" : undefined}
              testid="post-tax-liability" />
            <BigStat label="Total CMV"
              value={inrFull(postTax.total_current_value)}
              testid="post-tax-cmv" />
          </div>

          {/* Per-property tax cards */}
          {taxOn && postTax.per_property?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-3 mt-5" data-testid="post-tax-property-grid">
              {postTax.per_property.map((p) => (
                <div key={p.id} className="border hairline p-3 text-xs">
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      p.tax?.type === "LTCG" ? "bg-green-500/10 text-green-700" :
                      p.tax?.type === "STCG" ? "bg-amber-500/10 text-amber-700" :
                      "bg-muted/30 text-muted-foreground"
                    }`}>{p.tax?.type || "—"}</span>
                  </div>
                  <div className="text-muted-foreground">{p.city}</div>
                  <div className="mt-1 grid grid-cols-2 gap-1">
                    <span className="text-muted-foreground">If sold today, est. tax:</span>
                    <span className="text-right font-medium">{inr(p.tax?.liability || 0)}</span>
                    {p.tax?.indexed_cost && (<>
                      <span className="text-muted-foreground">Indexed cost ({p.tax.cii_purchase_year}→{p.tax.cii_sale_year}):</span>
                      <span className="text-right">{inr(p.tax.indexed_cost)}</span>
                    </>)}
                    {p.tax?.gain != null && (<>
                      <span className="text-muted-foreground">Taxable gain:</span>
                      <span className="text-right">{inr(p.tax.gain)}</span>
                    </>)}
                  </div>
                  {p.tax?.warning && (
                    <div className="mt-1 text-amber-600 text-[11px]">{p.tax.warning}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {taxOn && postTax.insights?.length > 0 && (
            <div className="mt-4 space-y-1 text-xs" data-testid="post-tax-insights">
              {postTax.insights.map((it) => (
                <div key={it.slice(0, 60)} className="text-muted-foreground">
                  <span className="text-[hsl(var(--primary))]">●</span> {it}
                </div>
              ))}
            </div>
          )}

          {taxOn && (
            <div className="text-[10px] text-muted-foreground mt-4 border-t hairline pt-3 italic"
              data-testid="post-tax-disclaimer">
              {postTax.disclaimer}
            </div>
          )}
        </div>
      )}

      {/* === Auto Snapshot Trend === */}
      {snaps && snaps.items.length >= 2 && (
        <div className="card-flat p-6" data-testid="auto-snapshot-section">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="eyebrow mb-1">Wealth trend · auto-tracked</div>
              <div className="font-serif text-2xl">
                Your net worth, month after month.
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {snaps.items.length} monthly snapshots · captured on first login
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={snaps.items.map((s) => ({
                month: s.snapshot_month,
                value: s.total_portfolio_value,
                equity: s.total_equity,
                loans: s.total_loans,
              }))}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => inr(v)} />
                <Tooltip formatter={(v) => inrFull(v)} />
                <Legend />
                <Line type="monotone" dataKey="value" name="Portfolio value" stroke="#0B7B6E" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="equity" name="Equity (net worth)" stroke="#D97706" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="loans" name="Loans" stroke="#DC2626" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                {goalAmount && <ReferenceLine y={goalAmount} stroke="#6B7280" strokeDasharray="4 4" label="Goal" />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {snaps.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs" data-testid="snapshot-summary">
              <Stat k="Net worth growth" v={`${inr(snaps.summary.equity_growth_abs)} (${snaps.summary.equity_growth_pct > 0 ? "+" : ""}${snaps.summary.equity_growth_pct}%)`} />
              <Stat k="Equity CAGR" v={`${snaps.summary.cagr_pct}% p.a.`} />
              <Stat k="Loan reduction" v={inr(snaps.summary.loan_reduction)} />
              <Stat k="Months tracked" v={snaps.summary.months_count} />
            </div>
          )}
        </div>
      )}

      {/* === AI Advisor === */}
      <div className="card-flat p-6" data-testid="ai-advisor-section">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="eyebrow mb-1 inline-flex items-center gap-2">
              <Sparkle size={12} weight="duotone" /> Your AI Portfolio Advisor
            </div>
            <div className="font-serif text-2xl">
              Ranked moves, grounded in your data.
            </div>
            {advisor?.generated_at && (
              <div className="text-[11px] text-muted-foreground mt-1">
                Analysis as of {new Date(advisor.generated_at).toLocaleDateString("en-IN")}
                {advisor.age_days != null && ` · ${advisor.age_days}d ago`}
                {advisor.cached && " · cached"}
              </div>
            )}
          </div>
          <button
            onClick={generateAdvisor}
            disabled={advisorBusy}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="ai-advisor-generate">
            <ArrowsClockwise size={14} weight="bold" className={advisorBusy ? "animate-spin" : ""} />
            {advisor?.recommendations?.length > 0 ? "Generate new insights" : "Generate insights"}
          </button>
        </div>

        {advisor?.error && (
          <div className="text-xs text-amber-600 border border-amber-500/30 bg-amber-500/10 p-3"
            data-testid="ai-advisor-error">{advisor.error}</div>
        )}
        {advisor?.empty && (
          <div className="text-xs text-muted-foreground italic" data-testid="ai-advisor-empty">
            Add at least one owned property to your portfolio to unlock personalised AI recommendations.
          </div>
        )}
        {advisor?.recommendations?.length > 0 && (
          <div className="grid md:grid-cols-2 gap-3" data-testid="ai-advisor-cards">
            {advisor.recommendations.map((r) => (
              <AdvisorCard key={`${r.rank}-${r.title}`} r={r} />
            ))}
          </div>
        )}
        {!advisor && (
          <div className="text-xs text-muted-foreground italic">
            No insights yet — click <b>Generate insights</b> for a personalised review of your portfolio.
          </div>
        )}
      </div>

      {settingsOpen && (
        <TaxSettingsModal
          current={postTax}
          onClose={() => setSettingsOpen(false)}
          onSaved={async () => {
            setSettingsOpen(false);
            const { data } = await api.get("/networth/post-tax");
            setPostTax(data);
          }}
        />
      )}
    </div>
  );
}

function BigStat({ label, value, accent, testid }) {
  const tone = accent === "warn" ? "text-amber-600" : "";
  return (
    <div className="border hairline p-3">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="border hairline p-2">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{k}</div>
      <div className="font-medium mt-0.5">{v}</div>
    </div>
  );
}

function AdvisorCard({ r }) {
  const urgencyColor = {
    "Immediate": "bg-red-500/10 text-red-700 border-red-500/30",
    "Within 3 months": "bg-amber-500/10 text-amber-700 border-amber-500/30",
    "Long-term planning": "bg-teal-500/10 text-teal-700 border-teal-500/30",
  }[r.urgency] || "bg-muted/30";
  return (
    <div className="border hairline p-4 space-y-2" data-testid={`advisor-card-${r.rank}`}>
      <div className="flex items-start gap-3">
        <div className="h-7 w-7 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-bold flex items-center justify-center shrink-0">
          {r.rank}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium leading-snug">{r.title}</div>
          <div className="text-xs text-muted-foreground mt-1">{r.reasoning}</div>
        </div>
      </div>
      {r.action && (
        <div className="bg-amber-500/10 border border-amber-500/30 px-2 py-1.5 text-xs">
          <span className="text-amber-700 font-medium">Action: </span>
          <span>{r.action}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className={`px-2 py-0.5 rounded-full border ${urgencyColor}`}>{r.urgency}</span>
        {r.impact_estimate && (
          <span className="text-green-700">
            <Bank size={11} className="inline mr-1" weight="duotone" />
            {r.impact_estimate}
          </span>
        )}
      </div>
    </div>
  );
}

function TaxSettingsModal({ current, onClose, onSaved }) {
  const [regime, setRegime] = useState(current?.tax_regime || "old");
  const [slab, setSlab] = useState(current?.income_slab || "10_15L");
  const [sec54, setSec54] = useState(current?.sec54_enabled || false);
  const save = async () => {
    await api.post("/networth/tax-settings", {
      tax_regime: regime, income_slab: slab, sec54_enabled: sec54,
    });
    onSaved();
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="tax-settings-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-md w-full p-5 space-y-4">
        <div>
          <div className="eyebrow">Tax settings</div>
          <h3 className="font-serif text-2xl mt-1">How should we tax you?</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Drives the LTCG / indexation calculation in Post-tax view.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium mb-1">Tax regime</div>
            <div className="inline-flex border hairline rounded overflow-hidden text-xs">
              <button onClick={() => setRegime("old")}
                className={`px-3 py-1.5 ${regime === "old" ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                data-testid="tax-regime-old">Old (with indexation, 20%)</button>
              <button onClick={() => setRegime("new")}
                className={`px-3 py-1.5 ${regime === "new" ? "bg-[hsl(var(--primary))] text-white" : ""}`}
                data-testid="tax-regime-new">New (flat 12.5%)</button>
            </div>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Income slab (drives surcharge)</div>
            <select value={slab} onChange={(e) => setSlab(e.target.value)}
              className="bg-background border hairline px-2 py-1.5 text-xs w-full"
              data-testid="tax-income-slab">
              <option value="up_7L">Up to ₹7L</option>
              <option value="7_10L">₹7-10L</option>
              <option value="10_15L">₹10-15L</option>
              <option value="above_15L">Above ₹15L (HNI surcharge applies)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={sec54} onChange={(e) => setSec54(e.target.checked)}
              data-testid="tax-sec54-toggle" />
            Plan to reinvest in residential property within 2 years (Section 54 / 54F)
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs"
            data-testid="tax-settings-cancel">Cancel</button>
          <button onClick={save} className="btn-primary px-3 py-1.5 text-xs"
            data-testid="tax-settings-save">Save</button>
        </div>
      </div>
    </div>
  );
}
